import AutocompleteTag from 'components/AutocompleteTag';
import InfoTooltip from 'components/InfoTooltip';
import { EllipsisTooltip } from 'components/misc/EllipsisTooltip';
import { FormattedDate } from 'components/misc/FormattedDate';
import { EmployeeNameLink, RecordLink } from 'components/record-chip/RecordLink';
import React, { isValidElement } from 'react';
import { FieldValues } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';

/** One value of a multi-value row, drawn as the chip its multi-select draws in a record section. */
export interface RowChip {
    key: React.Key;
    label: React.ReactNode;
    /** Before the label, as a multi-select's lock on a fixed value. */
    icon?: React.ReactNode;
    /** After the label, as a marker the multi-select's chip also carries. */
    adornment?: React.ReactNode;
    /** The label is a link to a record, which is a chip of its own: drawn in place of the section's chip. */
    record?: boolean;
}

export interface Row {
    label: React.ReactNode | string;
    content?: string | React.ReactNode;
    /** Not rendered: a row with a `url` shows its label and content like any other row. */
    url?: string;
    button?: React.ReactNode;
    hidden?: boolean;
    icon?: React.ReactNode;
    noDivider?: boolean;
    /**
     * Keeps the line breaks of a string `content`. In the `isNew` grid it spans the whole row while it has a value,
     * as a text area does: see {@link spansRow}.
     */
    multiline?: boolean;
    /**
     * In the `isNew` grid, spans the whole row while it has a value, as a multiline row does. `'always'` spans it
     * with or without one, for a field whose control needs the row whatever it holds, as a radio group's options do.
     */
    wide?: boolean | 'always';
    /**
     * In the `isNew` grid, the values of a multi-select, on a row of their own; with none, `content` is drawn, in
     * one cell.
     */
    chips?: RowChip[];
    /**
     * No field of the section's form edits this row. The `isNew` grid draws it after the rows a field edits, and
     * it stays as text under the fields while the section edits. A function is such a row whose value a field takes
     * over on some edit: handed the form's values, it says whether the row still stands, so the value never shows
     * in the row and in a field at once.
     */
    readOnly?: boolean | ((formValues: FieldValues) => boolean);
    /**
     * The create form's labels for the fields this row shows, where the row is labelled otherwise: a draft names
     * the values it was saved without by the form's labels (see `withMissing`).
     */
    formLabels?: string[];
}

interface GridItemListProps {
    noContainer?: boolean;
    listItemClassName?: string;
    data: Row[];
    twoPerRow?: boolean;
    titleId?: string;
    /** Not read: every list spans its whole row. */
    size?: number;
    className?: string;
    button?: React.ReactNode;
    shouldTranslate?: boolean;
    /** The new design's section card: label over value in a grid of up to three columns, without rules.
     *  `noContainer`, `twoPerRow` and `listItemClassName` belong to the classic list and do not apply. */
    isNew?: boolean;
}

/** A label over its value, as one cell of a row. */
function ItemContent({ row }: { row?: Row }) {
    const isMultiline = row?.multiline && typeof row.content === 'string' && row.content !== '-';
    return (
        <div className="my-1 min-w-0 flex-auto">
            <div className="text-fg-secondary pb-[5px]">{row?.label}</div>
            <span className={cn('text-fg flex flex-wrap text-sm', isMultiline && 'break-words whitespace-pre-wrap')}>
                {row?.content}
            </span>
        </div>
    );
}

function ItemAside({ children, className }: { children: React.ReactNode; className?: string }) {
    return <div className={cn('text-fg inline-flex min-w-6 shrink-0', className)}>{children}</div>;
}

// `grid-item-list-row` carries the row's link colour (index.css).
const ITEM_CLASS =
    'grid-item-list-row border-divider mx-4 flex w-[calc(100%-32px)] items-center px-0 py-2 text-sm leading-[1.5715]';

export default function GridItemList({
    noContainer = false,
    listItemClassName,
    data,
    twoPerRow,
    titleId,
    className,
    button,
    shouldTranslate = true,
    isNew = false,
}: GridItemListProps) {
    if (isNew) {
        return (
            <GridCard
                data={data}
                titleId={titleId}
                className={className}
                button={button}
                shouldTranslate={shouldTranslate}
            />
        );
    }
    const content = (
        <ul className="bg-paper w-full py-0">
            {titleId ? (
                <li className="border-divider flex items-center border-b px-4 py-2 last:border-b-0">
                    <div className="my-1 min-w-0 flex-auto text-base leading-6 font-semibold">
                        {shouldTranslate ? <FormattedMessage id={titleId} /> : titleId}
                    </div>
                    {button ?? null}
                </li>
            ) : null}
            {data.map((row, index) => {
                if (row.hidden) {
                    return null;
                }

                if (twoPerRow) {
                    if ((index + 1) % 2 === 0) return null;
                    const next = data[index + 1];
                    return (
                        <li
                            className={cn(
                                ITEM_CLASS,
                                index !== data.length - 1 && 'border-b last:border-b-0',
                                listItemClassName
                            )}
                            // oxlint-disable-next-line react-doctor/no-array-index-as-key -- Row has no stable id; list is not reordered/filtered after mount
                            key={`item-${index}`}
                        >
                            <div className="flex w-1/2 items-center justify-center pr-6">
                                <ItemContent row={row} />
                                {row.button ? <ItemAside>{row.button}</ItemAside> : null}
                            </div>
                            <div className="flex w-1/2 items-center justify-center">
                                <ItemContent row={next} />
                                {next?.button ? <ItemAside>{next.button}</ItemAside> : null}
                            </div>
                        </li>
                    );
                }
                return (
                    <li
                        className={cn(
                            ITEM_CLASS,
                            !row.noDivider && index !== data.length - 1 && 'border-b last:border-b-0',
                            listItemClassName
                        )}
                        // oxlint-disable-next-line react-doctor/no-array-index-as-key -- Row has no stable id; list is not reordered/filtered after mount
                        key={`item-${index}`}
                    >
                        {row.icon ? <ItemAside className="mr-4">{row.icon}</ItemAside> : null}
                        <ItemContent row={row} />
                        {row.button ? <ItemAside>{row.button}</ItemAside> : null}
                    </li>
                );
            })}
        </ul>
    );
    return (
        <div className={cn('w-full', className)}>
            {noContainer ? (
                content
            ) : (
                <div className="border-divider bg-card new:border-0 new:shadow-card new:outline-hidden h-full overflow-hidden rounded-[10px] border">
                    {content}
                </div>
            )}
        </div>
    );
}

/**
 * The new design's section card: a 15px title with the section's action at its right, over the body. A container,
 * so the body can lay its columns out on the card's own width rather than the window's.
 */
export function SectionCard({
    title,
    action,
    className,
    children,
}: {
    /** Without one there is no header row, and no action. */
    title?: React.ReactNode;
    action?: React.ReactNode;
    className?: string;
    children: React.ReactNode;
}) {
    return (
        <section
            className={cn(
                'border-border bg-card new:border-0 new:shadow-card new:outline-hidden @container flex flex-col gap-3.5 rounded-[10px] border px-5 pt-4 pb-[18px]',
                className
            )}
        >
            {title ? (
                <div className="flex min-h-[30px] items-center gap-2">
                    <h3 className="text-section m-0 min-w-0 flex-auto">{title}</h3>
                    {action ?? null}
                </div>
            ) : null}
            {children}
        </section>
    );
}

/**
 * The field grid of a record section, in read and in edit: three columns on a wide card, two on a narrow one, one
 * below 288px.
 */
export const FIELD_GRID_CLASS = 'grid grid-cols-1 gap-x-6 gap-y-2.5 @2xs:grid-cols-2 @2xl:grid-cols-3';

/** A field's label, in read and in edit: 12px on a 16px line. */
export const FIELD_LABEL_CLASS = 'text-muted-foreground text-xs leading-4';

/**
 * For the line a label sits on: a control beside the text, such as an info icon or a field's History button, is
 * centred on the line without growing it, so the values of one grid row stay level.
 */
export const FIELD_LABEL_LINE_CLASS = '[&_button]:-my-1 [&_svg]:-my-0.5';

/**
 * Whether, in the field grid, a cell whose value may need the whole row takes it, such as a text's, a list of
 * chips' or an address's: it does while there is a value to lay out on it, and is one cell while there is none,
 * which is nothing, an empty text, the dash a read row prints, or a list of none or of those. A read cell goes by
 * its row's value and a field by the value its form opened with, so the two agree.
 */
export const spansRow = (value: unknown): boolean =>
    Array.isArray(value) ? value.some(spansRow) : !(value == null || value === '' || value === '-');

/** How a row's value box is drawn; each is the box of the control that edits such a value. */
export type CellBox = 'chips' | 'multiline' | 'line' | 'free';

const ONE_LINE_TYPES: unknown[] = ['a', Link, RecordLink, EmployeeNameLink, FormattedDate, EllipsisTooltip];

/**
 * A string, a number, a link or a date is one line of text, as a text field, a select or a date field shows it;
 * so is a node its row wraps in an `EllipsisTooltip`. Any other node draws itself, from the same first line down.
 */
export function cellBox({ content, chips, multiline }: Row): CellBox {
    if (chips?.length) {
        return 'chips';
    }
    if (typeof content === 'string' || typeof content === 'number') {
        return multiline ? 'multiline' : 'line';
    }
    return isValidElement(content) && ONE_LINE_TYPES.includes(content.type) ? 'line' : 'free';
}

function isEllipsisTooltip(content: React.ReactNode) {
    return isValidElement(content) && content.type === EllipsisTooltip;
}

// The box a field's control takes in edit, without its chrome: 36px, hanging 11px out of the cell on the left
// where the control has its border and padding, so the value text starts at the cell's edge in both.
const BOX_CLASS = 'text-foreground -ml-[11px] flex w-[calc(100%+11px)] min-w-0 gap-2 px-[11px] text-sm font-medium';

const BOX_CLASSES: Record<CellBox, string> = {
    line: 'h-9 items-start py-2',
    multiline: 'min-h-9 items-start py-2 break-words whitespace-pre-wrap',
    free: 'min-h-9 items-start py-2 break-words',
    chips: 'min-h-9 flex-wrap items-center gap-1.5 py-[7px]',
};

/**
 * The cells of the new design's field grid: a label over its value's box. A cell is as tall in read as its field is
 * in edit, and its label and value text sit where the field draws them.
 */
export function GridCells({ rows }: { rows: Row[] }) {
    return rows.map((row, index) => {
        if (row.hidden) {
            return null;
        }
        const box = cellBox(row);
        return (
            <div
                // oxlint-disable-next-line react-doctor/no-array-index-as-key -- Row has no stable id; rows do move after mount (a read-only one goes last, a rule drops one), which at worst resets or reuses a cell's transient state, such as a tooltip's
                key={`item-${index}`}
                className={cn(
                    'flex min-w-0 flex-col gap-1',
                    // A multi-select's value is its chips, whatever its row draws for none.
                    (row.wide === 'always' ||
                        (row.chips ? spansRow(row.chips) : (row.wide || row.multiline) && spansRow(row.content))) &&
                        'col-span-full'
                )}
            >
                {/* A multi-select's label leaves the end of its line to a picker's button; so does this one. */}
                <dt className={cn(FIELD_LABEL_CLASS, FIELD_LABEL_LINE_CLASS, '*:items-center', row.chips && 'pr-7')}>
                    {row.label}
                </dt>
                <dd className={cn(BOX_CLASS, BOX_CLASSES[box])}>
                    {box === 'chips' ? (
                        <>
                            {row.chips?.map(({ key, record, ...chip }) =>
                                record ? (
                                    <span key={key} className="inline-flex max-w-full min-w-0 items-center gap-1">
                                        {chip.icon}
                                        {chip.label}
                                        {chip.adornment}
                                    </span>
                                ) : (
                                    <AutocompleteTag key={key} section {...chip} />
                                )
                            )}
                            {/* Where the multi-select has its clear button and its chevron, so the chips wrap
                                onto the same lines in both. */}
                            <span aria-hidden className="h-5 w-12 flex-none" />
                        </>
                    ) : (
                        // A multiline value stops near 72 characters a line, however wide its card.
                        <div className={cn('min-w-0 flex-auto', box === 'multiline' && 'max-w-[60ch]')}>
                            {box === 'line' && !isEllipsisTooltip(row.content) ? (
                                <EllipsisTooltip>{row.content}</EllipsisTooltip>
                            ) : (
                                row.content
                            )}
                        </div>
                    )}
                    {row.button ?? null}
                </dd>
            </div>
        );
    });
}

function GridCard({
    data,
    titleId,
    className,
    button,
    shouldTranslate,
}: Pick<GridItemListProps, 'data' | 'titleId' | 'className' | 'button' | 'shouldTranslate'>) {
    const title = titleId && shouldTranslate ? <FormattedMessage id={titleId} /> : titleId;
    return (
        <SectionCard title={title} action={button} className={className}>
            <dl className={cn(FIELD_GRID_CLASS, 'm-0')}>
                <GridCells rows={[...data.filter((row) => !row.readOnly), ...data.filter((row) => row.readOnly)]} />
            </dl>
        </SectionCard>
    );
}

/**
 * A new-design row's label with its field's info icon, laid out as the field lays the two out, so the label wraps
 * in the card where it wraps in the section's form. `className` is how the field spaces them: the icon after the
 * text, or at the end of the label's line.
 */
export const infoLabel = (labelId: string, tooltipId: string, className = 'gap-1.5', small?: boolean) => (
    <span className={cn('flex items-center', className)}>
        <span>{t(labelId)}</span>
        <InfoTooltip text={tooltipId} label={t(labelId)} small={small} />
    </span>
);

/** `infoLabel` with the small (i) that keeps the label's 16px line, as the invoice record's read views draw it. */
export const smallInfoLabel = (labelId: string, tooltipId: string) => infoLabel(labelId, tooltipId, 'gap-1.5', true);

function filterRowList(rows: (Row | null)[]): Row[] {
    return rows.filter((row) => !!row) as Row[];
}

export { filterRowList };
